import React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useApp } from "../context";
import { Window, Connect } from "../components/UI";
import { demoActivities, routePoints } from "../utils/activities";
const messages = {
  not_configured:
    "Strava connection setup is still in progress. Explore the demo in the meantime.",
  access_denied:
    "Connection cancelled. You can connect again whenever you’re ready.",
  invalid_state: "That connection attempt expired. Please start a new one.",
  permission_denied:
    "Please allow activity access on Strava so we can create your recap.",
  authorization_failed:
    "Strava couldn’t complete your connection. Please try again.",
  upstream_unavailable:
    "Strava is temporarily unavailable. Please try again shortly.",
  rate_limited:
    "Strava’s request limit has been reached. Please try connecting later.",
  session_expired: "Your connection expired. Please reconnect.",
  server_error: "The connection could not be completed. Please try again.",
};
export default function Home() {
  const { startDemo, profile } = useApp();
  const history = useHistory();
  const code = new URLSearchParams(useLocation().search).get("connection");
  const sample = demoActivities(2025).slice(0, 9);
  return (
    <div className="page home">
      <div className="hero">
        <div className="hero-copy">
          <p className="eyebrow">
            <span className="status-dot" /> A LITTLE NOSTALGIA. A LOT OF MILES.
          </p>
          <h1>
            You went places.
            <br />
            <span>Let’s look back.</span>
          </h1>
          <p className="hero-description">
            The early starts. The long way home. The “just one more mile.” Turn
            your Strava activities into a year worth keeping.
          </p>
          <div className="hero-actions">
            {profile ? (
              <Link className="button primary" to="/yourwrapped">
                Open your year ↗
              </Link>
            ) : (
              <Connect />
            )}
            <button
              className="button"
              onClick={() => {
                startDemo();
                history.push("/yourwrapped");
              }}
            >
              Explore a demo <span>↗</span>
            </button>
          </div>
          <p className="fine-print">
            Read-only access. Your activities stay yours.
          </p>
          {code && (
            <div className="notice" role="alert">
              {messages[code] ||
                "Something interrupted the connection. Please try again."}{" "}
              <Link to="/about#connection-help">Connection help ↗</Link>
            </div>
          )}
          <div className="hero-notes">
            <span>01 / CONNECT</span>
            <span>02 / REWIND</span>
            <span>03 / MAKE ART</span>
          </div>
        </div>
        <div className="hero-art">
          <span className="sticker">
            EVERY MILE
            <br />
            HAS A STORY.
          </span>
          <Window title="a_year_well_spent.svg" className="poster-window">
            <div className="sample-poster">
              <div className="poster-heading">
                <span>
                  THE YEAR
                  <br />
                  YOU SHOWED UP.
                </span>
                <span className="poster-year">’25</span>
              </div>
              <svg
                className="poster-routes"
                viewBox="0 0 600 600"
                aria-label="Example artwork made from nine sample routes"
              >
                {sample.map((a, i) => (
                  <g
                    key={a.id}
                    transform={`translate(${(i % 3) * 200},${Math.floor(i / 3) * 200})`}
                  >
                    <polyline
                      points={routePoints(a)}
                      fill="none"
                      stroke={
                        i % 3 === 0
                          ? "#c35538"
                          : i % 3 === 1
                            ? "#2f6655"
                            : "#29271f"
                      }
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                ))}
              </svg>
              <div className="poster-bottom">
                <span>108 ACTIVITIES · COUNTLESS GOOD DAYS</span>
                <span>STRAVA WRAPPED ↗</span>
              </div>
            </div>
            <div className="window-status">
              Example routes <span>9 files · a whole lot of memories</span>
            </div>
          </Window>
          <div className="annotation">Your routes. A little work of art. ↖</div>
        </div>
      </div>
      <section className="feature-strip" aria-label="What you can make">
        {[
          [
            "01",
            "Your year, all together",
            "Miles, moving time, big climbs, and the days you kept showing up.",
          ],
          [
            "02",
            "See your rhythm",
            "Find your busiest month and the activities that made your year.",
          ],
          [
            "03",
            "Make something yours",
            "Pick your routes, choose a style, and download your own artwork.",
          ],
        ].map(([n, title, copy]) => (
          <article key={n}>
            <span className="feature-number">{n} ↗</span>
            <h2>{title}</h2>
            <p>{copy}</p>
          </article>
        ))}
      </section>
      <section className="how-it-works">
        <div>
          <p className="eyebrow">NO SPREADSHEETS REQUIRED</p>
          <h2>
            A trip down
            <br />
            memory lane.
          </h2>
        </div>
        <ol>
          <li>
            <b>Connect your Strava.</b>
            <p>
              Approve read-only access to the activities you want to include.
            </p>
          </li>
          <li>
            <b>Pick a year.</b>
            <p>
              Explore your numbers, switch sports, and find your favorite
              moments.
            </p>
          </li>
          <li>
            <b>Keep the good stuff.</b>
            <p>
              Save your recap as a PDF or turn your routes into a printable
              poster.
            </p>
          </li>
        </ol>
      </section>
    </div>
  );
}
